// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Quaternion from '../../../sumerian/math/Quaternion';
import Vector3 from '../../../sumerian/math/Vector3';
import TransformChannel from '../../animationpack/clip/TransformChannel';
import JointData from '../../animationpack/clip/JointData';

/**
 * Transform animation channel, specifically geared towards describing the motion of skeleton joints.
 */
class JointChannel extends TransformChannel {
 /**
  * @param {string} jointName our joint name.
  * @param {Array<number>} times our time offset values.
  * @param {Array<number>} rotations the rotations to set on this channel at each time offset.
  * @param {Array<number>} translations the translations to set on this channel at each time offset.
  * @param {Array<number>} scales the scales to set on this channel at each time offset.
  * @param {string} blendType
  * @param {?Array<number>} additiveBaseRotation Array[4] for the rotation quaternion of the additive base pose
  * @param {?Array<number>} additiveBaseTranslation Array[3] for the translation of the additive base pose
  * @param {?Array<number>} additiveBaseScale Array[3] for the scale of the additive base pose
  */
 constructor(jointName, times, rotations, translations, scales, blendType,
             additiveBaseRotation, additiveBaseTranslation, additiveBaseScale) {
   super(jointName, times, rotations, translations, scales, blendType);

   if (typeof jointName !== 'string') {
     throw new Error('[Sumerian] The JointChannel constructor no longer accepts a joint index. Please see the Sumerian API documentation for more information: https://content.sumerian.amazonaws.com/engine/latest/doc/');
   }

   // Joint skeleton node path used to bind to skeleton nodes in AnimationClipInstance.getApplyTo()
   this._jointName = jointName;

   // note that additiveBase transformation data can be undefined for channels saved prior to Nov 2018
   this._additiveBaseRotation = additiveBaseRotation ? new Quaternion(additiveBaseRotation) : null;
   this._additiveBaseTranslation = additiveBaseTranslation ? new Vector3(additiveBaseTranslation) : null;
   this._additiveBaseScale = additiveBaseScale ? new Vector3(additiveBaseScale) : null;
 }

 /**
  * Creates a data item for this type of channel
  * @returns {JointData}
  */
 createStateDataObject() {
   return new JointData();
 }

 /**
  * Applies the channels animation state to supplied data item
  * @param {number} sampleIndex
  * @param {number} progressPercent
  * @param {JointData} jointData The data item to apply animation to
  */
 setCurrentSample(sampleIndex, progressPercent, jointData) {
   super.setCurrentSample(sampleIndex, progressPercent, jointData);
 }

 /**
  * Apply a specific index of this channel to a {@link JointData} object.
  * @param {number} index the index to grab.
  * @param {JointData} [store] the TransformData to store in. If null, a new one is created.
  * @returns {JointData} our resulting TransformData.
  */
 getData(index, store) {
   const rVal = store || new JointData();
   super.getData(index, rVal);
   return rVal;
 }
}

/**
 * @type {string}
 * @readonly
 * @default '_jnt'
 */
JointChannel.JOINT_CHANNEL_NAME = '_jnt';

export default JointChannel;
